import { createApiClient, apiClient } from "../../api/apiClient";
import { AppDispatch } from "../../store";
import { setfetchedQuestions } from "../../store/slices/pulseCheckSlice";
import { fetchObservationDetails } from "./fetchObservationDetailsUtil";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";

/**
 * Fetches pulse check questions based on the selected role and observation type
 * @param selectedFilters - Object containing role and observationType
 * @param pulseState - Current state of the pulse check ('new', 'in-progress', or 'completed')
 * @param selectedRecord - Selected observation record if available
 * @param dispatch - Redux dispatch function
 * @param setError - Function to set error message
 * @param setLoading - Function to toggle loading state
 */
export const fetchQuestions = async (
  selectedFilters: { role: string; observationType: string },
  pulseState: string,
  selectedRecord: { id: number } | null,
  dispatch: AppDispatch,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void
) => {
  const { role, observationType } = selectedFilters;

  if (!role || !observationType) {
    setError("Invalid Role or Observation Type");
    setLoading(false);
    return;
  }

  setError(null);
  setLoading(true);

  try {
    const api = createApiClient();
    console.log('pulse state:', pulseState);
    // For existing observations (in-progress or completed), skip org context params
    // For new observations, allow the interceptor to add org context params
    const shouldSkipOrgContext = pulseState === 'completed';
    const hasExistingRecord = !!selectedRecord?.id && pulseState === 'in-progress';

    const recordIdParam = hasExistingRecord ? `&recordId=${selectedRecord!.id}` : '';
    const requestConfig = shouldSkipOrgContext
      ? { _skipOrgContextForQuestions: true }
      : undefined;

    const response = await apiClient(
      api,
      `/observations/questions?role=${encodeURIComponent(
        role
      )}&observation_type=${encodeURIComponent(observationType)}${recordIdParam}`,
      "GET",
      undefined,
      requestConfig
    );

    if (response && response.data) {
      console.log("response", response);
      console.log("Questions received:", response.data);
      // Update Redux state with fetched questions
      dispatch(setfetchedQuestions(response.data));
      setError(null);

      // If pulse state is 'in-progress' or 'completed', fetch additional data
      if (
        (pulseState === "in-progress" || pulseState === "completed") &&
        selectedRecord?.id
      ) {
        console.log(
          `PulseCheck is in '${pulseState}' state. Fetching saved observation data...`
        );
        await fetchObservationDetails(
          selectedRecord.id,
          response.data,
          dispatch,
          pulseState
        );
      }
    } else {
      // API returned success but no data - treat as error
      console.error("API returned success but no questions data");
      const userFacingError = "Unable to load questions. Please try again.";
      
      handleObservationErrorWithFriendlyMessage(
        OBSERVATION_API_NAMES.FETCH_OBSERVATION_QUESTIONS,
        userFacingError,
        true,
        {
          role,
          observationType,
          pulseState,
          reason: "Empty response data"
        }
      );
      
      setError(userFacingError);
    }
  } catch (e) {
    console.error("Error fetching questions:", e);
    
    const userFacingError = "Unable to load questions. Please try again.";
    // Show error overlay modal with the user-facing message
    handleObservationErrorWithFriendlyMessage(
      OBSERVATION_API_NAMES.FETCH_OBSERVATION_QUESTIONS,
      userFacingError,
      true, // Show modal overlay
      {
        role,
        observationType,
        pulseState,
        originalError: e,
      }
    );
    
    setError(userFacingError);
  } finally {
    setLoading(false);
  }
};
